<template>
  <Card>
    <div slot="body"
         class="ship-scope">
      <div class="card-title-row">
        <h5 class="header-text text-color-4 mb-0">{{ $t("messenger.ship.card_title") }}</h5>
        <button v-if="pagesState === 'ready' && pages.length"
                type="button"
                class="btn btn-outline-primary"
                @click="toggleExamples">{{ showExamples ? $t("messenger.ship.hide_examples") : $t("messenger.ship.show_examples") }}</button>
      </div>

      <Spinner v-if="pagesState === 'loading'"
               size="sm" />

      <div v-else-if="pagesState === 'error'"
           class="alert alert-warning d-flex align-items-center flex-wrap mb-0 unavailable">
        <span><i class="fal fa-exclamation-triangle mr-1"></i>{{ $t("messenger.unavailable") }}</span>
        <button type="button"
                class="btn btn-outline-primary btn-sm ml-auto"
                @click="loadPages()">{{ $t("messenger.retry") }}</button>
      </div>

      <template v-else>
        <template v-if="!pages.length">
          <FbExamples />
          <Button variant="primary"
                  size="lg"
                  custom_class="ship-connect mt-3"
                  :loading="connecting"
                  @click="connect">
            <i v-if="!connecting"
               class="fab fa-facebook mr-1"></i>{{ $t("messenger.ship.connect") }}
          </Button>
        </template>

        <template v-else>
          <FbExamples v-if="showExamples" />
          <div class="page-list mt-3">
            <PageRow v-for="page in paginatedPages"
                     :key="page.pageId + ':' + bumpOf(page.pageId)"
                     :page="page"
                     @view-message="openSheet"
                     @change-shop="goChoice"
                     @reconnect="connect"
                     @how-to="howTo = true" />
          </div>
          <div class="d-flex justify-content-between align-items-center flex-wrap mt-3">
            <div>
              <select v-model.number="perPage"
                      class="form-control"
                      @change="page = 1">
                <option v-for="n in perPageOptions"
                        :key="n"
                        :value="n">{{ n }}</option>
              </select>
            </div>

            <div class="d-flex align-items-center">
              <p class="align-self-center mb-0 mr-2 text-color-4">
                Page {{ page }} / {{ lastPage }}
              </p>

              <div class="btn-group"
                   role="group">
                <Button type="button"
                        variant="outline-primary"
                        size="lg"
                        :disabled="page === 1"
                        @click="paginate(false)">
                  <i class="fal fa-long-arrow-left"></i>
                </Button>

                <Button type="button"
                        variant="outline-primary"
                        size="lg"
                        :disabled="page >= lastPage"
                        @click="paginate(true)">
                  <i class="fal fa-long-arrow-right"></i>
                </Button>
              </div>
            </div>
          </div>
          <button type="button"
                  class="btn btn-outline-primary mt-3"
                  :disabled="connecting"
                  @click="connect">{{ $t("messenger.ship.connect_more") }}</button>
        </template>
      </template>

      <TrackingMessageSheet v-if="sheetPage"
                            :page="sheetPage"
                            @close="closeSheet"
                            @saved="refreshRow"
                            @reconnect="connect" />
      <ShipHowToSheet v-if="howTo"
                      @close="howTo = false" />
    </div>
  </Card>
</template>

<script>
import Card from "@/components/core/Card";
import Button from "@/components/core/Button";
import Spinner from "@/components/core/Spinner";
import FbExamples from "@/components/derived/messenger/FbExamples";
import PageRow from "@/components/derived/messenger/PageRow";
import TrackingMessageSheet from "@/components/derived/messenger/TrackingMessageSheet";
import ShipHowToSheet from "@/components/derived/messenger/ShipHowToSheet";
import { rememberClaimShops } from "@/store/modules/messenger";
import { Toast } from "@/helpers/toastr";

const EXAMPLES_KEY = "shipExamples"; // "0" = hidden; anything else (or blocked storage) = open

// The เพจ Facebook card at the top of ตั้งค่า › การจัดส่ง. Owns the Pages list, the Facebook
// connect/claim return and the sheets; each row fetches its own settings and tracking summary.
export default {
  name: "FacebookPagesCard",
  components: { Card, Button, Spinner, FbExamples, PageRow, TrackingMessageSheet, ShipHowToSheet },

  data() {
    let showExamples = true;
    try {
      showExamples = window.localStorage.getItem(EXAMPLES_KEY) !== "0";
    } catch (e) {
      showExamples = true;
    }
    return {
      pagesState: "loading", // loading | ready | error
      pages: [],
      connecting: false,
      showExamples,
      sheetPage: null,
      howTo: false,
      templateBump: {}, // pageId -> n; bumped when the message sheet closes so the row re-reads
      page: 1,
      perPage: 5,
      perPageOptions: [5, 10, 20, 50],
    };
  },

  computed: {
    lastPage() {
      return Math.max(1, Math.ceil(this.pages.length / this.perPage));
    },

    paginatedPages() {
      const start = (this.page - 1) * this.perPage;
      return this.pages.slice(start, start + this.perPage);
    },
  },

  created() {
    // A claim after the Facebook redirect must finish before the list is read, or the new Pages are missing
    if (!this.handleConnectResult()) this.loadPages();
  },

  methods: {
    paginate(next) {
      if (next && this.page < this.lastPage) this.page += 1;
      else if (!next && this.page > 1) this.page -= 1;
    },

    bumpOf(pageId) {
      return this.templateBump[pageId] || 0;
    },

    toggleExamples() {
      this.showExamples = !this.showExamples;
      try {
        window.localStorage.setItem(EXAMPLES_KEY, this.showExamples ? "1" : "0");
      } catch (e) {
        // storage blocked: the toggle still works for this visit
      }
    },

    loadPages() {
      this.pagesState = "loading";
      this.$store.dispatch("messenger/fetchPages", {
        callback: (ok, pages) => {
          if (this._isDestroyed) return;
          if (!ok) {
            this.pagesState = "error";
            return;
          }
          this.pages = pages;
          this.page = 1;
          this.pagesState = "ready";
        },
      });
    },

    connect() {
      if (this.connecting) return;
      this.connecting = true;
      const redirectUrl = window.location.origin + window.location.pathname + "#/settings/shipping";
      this.$store.dispatch("messenger/connectUrl", {
        redirectUrl,
        callback: (ok, url) => {
          if (ok && url) {
            window.location.assign(url); // stays "connecting" while the browser leaves for Facebook
            return;
          }
          this.connecting = false;
          Toast(this.$t("messenger.pages.connect_failed"), "danger");
        },
      });
    },

    // Facebook sends the seller back to #/settings/shipping?connected=<n>&claim=<nonce> or ?error=<reason>.
    // Returns true when a claim is running; it loads the Pages list itself once the claim has settled.
    handleConnectResult() {
      const query = (this.$route && this.$route.query) || {};
      if (query.connected == null && query.error == null && query.claim == null && query.owned_by_other == null) return false;
      const ownedByOther = parseInt(query.owned_by_other, 10) || 0;

      // Drop connected/claim/error first, so a reload neither repeats the toast nor re-sends the claim
      const rest = Object.assign({}, query);
      delete rest.connected;
      delete rest.claim;
      delete rest.error;
      delete rest.owned_by_other;
      this.$router.replace({ name: "Seller-Settings-Shipping", query: rest }, () => {}, () => {});

      if (ownedByOther > 0) Toast(this.$t("messenger.pages.owned_by_other", { count: ownedByOther }), "warning");

      if (query.error) {
        const reason = ["cancelled", "expired", "failed"].includes(query.error) ? query.error : "failed";
        Toast(this.$t("messenger.pages.error_" + reason), reason === "cancelled" ? "info" : "danger");
        return false;
      }

      if (!query.claim) {
        this.toastConnected(parseInt(query.connected, 10) || 0);
        return false;
      }

      this.$store.dispatch("messenger/claimPages", {
        claim: String(query.claim),
        callback: (ok, result) => {
          if (this._isDestroyed) return;
          if (!ok) {
            const key = { claim_mismatch: "claim_mismatch", claim_expired: "claim_expired" }[result && result.code] || "error_failed";
            Toast(this.$t("messenger.pages." + key), "danger");
            this.loadPages();
            return;
          }
          const claimed = this.normalizeClaim(result);
          const needChoice = claimed.filter((page) => page.shopLink === "needs_choice");
          const failed = claimed.filter((page) => page.shopLink === "failed");
          const linked = claimed.filter((page) => page.shopLink === "linked" || page.shopLink === "already").length;
          needChoice.forEach((page) =>
            rememberClaimShops(page.pageId, { shops: page.shops, name: page.name, pictureUrl: page.pictureUrl })
          );
          const failReasons = [];
          failed.forEach((page) => {
            const reason = ["no_shop", "other_seller_shop", "not_seller", "proship_failed"].indexOf(page.reason) !== -1 ? page.reason : "proship_failed";
            if (failReasons.indexOf(reason) === -1) failReasons.push(reason);
          });
          failReasons.forEach((reason) => Toast(this.$t("messenger.ship.claim.reason_" + reason), "danger"));
          if (needChoice.length) {
            const restIds = needChoice.slice(1).map((page) => page.pageId).join(",");
            this.$router.push({
              name: "Seller-Settings-Shipping-Shop",
              params: { pageId: needChoice[0].pageId },
              query: restIds ? { next: restIds } : {},
            });
          } else if (linked > 0) {
            Toast(this.$t("messenger.pages.connected_toast", { count: linked }), "success");
          } else if (!failed.length) {
            Toast(this.$t("messenger.pages.connected_none"), "info");
          }
          this.loadPages();
        },
      });
      return true;
    },

    normalizeClaim(result) {
      const list = result && Array.isArray(result.pages) ? result.pages : Array.isArray(result) ? result : [];
      return list.map((page) => {
        const rawId = page.pageId != null ? page.pageId : page.page_id != null ? page.page_id : page.id;
        return {
          pageId: String(rawId),
          name: page.name || String(rawId),
          pictureUrl: page.profile_pic || page.pictureUrl || null,
          shopLink: page.shopLink || page.shop_link || null,
          shops: Array.isArray(page.shops) ? page.shops : [],
          reason: page.reason || null,
        };
      });
    },

    toastConnected(count) {
      if (count > 0) Toast(this.$t("messenger.pages.connected_toast", { count }), "success");
      else Toast(this.$t("messenger.pages.connected_none"), "info");
    },

    openSheet(page) {
      this.sheetPage = page;
    },

    // Re-read the Page's row; the sheet stays open (the รอเฟสอนุมัติ card must stay visible)
    refreshRow() {
      if (!this.sheetPage) return;
      const pageId = this.sheetPage.pageId;
      this.templateBump = Object.assign({}, this.templateBump, { [pageId]: this.bumpOf(pageId) + 1 });
    },

    closeSheet() {
      this.refreshRow();
      this.sheetPage = null;
    },

    goChoice(page) {
      this.$router.push({ name: "Seller-Settings-Shipping-Shop", params: { pageId: page.pageId } });
    },
  },
};
</script>

<style lang="scss" scoped>
.ship-scope .btn {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.unavailable {
  gap: 0.5rem;
}

.page-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

@media (max-width: 767.98px) {
  .ship-connect {
    width: 100%;
  }
}

body.--dark .ship-scope .btn-outline-primary {
  color: #8cc8f5;
  border-color: #8cc8f5;
}

body.--dark .ship-scope .btn-outline-primary:hover:not(:disabled) {
  color: #fff;
}

body:not(.--dark) .ship-scope .muted-note {
  color: #767676;
}
</style>
